import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { user } from "../ExperimentDAO";
import { getS3Object, parseCsvString, parseJsonString, pushNewS3Object } from "../dao";
import { fetchExperiments, fetchFiles, selectCurrentExperiment, setCurrentExperiment } from "../store/ExperimentSlice";
import { fetchIssues, issueCreated, issueNoteAdded, issueResolved, selectIssuesInExperiment, selectNextIssueId } from "../store/IssuesSlice";
import Graph from "./ExperimentGraph";
import FlagIssue from "./FlagIssue";
import { IssueEntry } from "./IssueEntry";
import BasicPopover from "./Popup";
import { Message } from "./entities/Message";
import { Notes } from "./entities/VesselNotes";
import { Issue } from "../entities/Issue";


export default function Details({ onSignOut }) {
  const dispatch = useDispatch()
  const signOut = onSignOut
  const location = useLocation();
  const navigate = useNavigate();
  const timestamp = location.pathname.replace('/access/', '');
  dispatch(setCurrentExperiment(timestamp))

  const [data, setData] = useState(null);
  const [notes, setNotes] = useState(null);
  const [issueEntries, setIssueEntries] = useState(null);

  const experimentDetails = useSelector(selectCurrentExperiment)
  const issuesInExperiment = useSelector(selectIssuesInExperiment)
  const nextIssueId = useSelector(selectNextIssueId)

  useEffect(() => {
    dispatch(fetchFiles()).then(filesAction => {
      dispatch(fetchExperiments(filesAction.payload)).then(experimentsAction => {
        dispatch(fetchIssues(experimentsAction.payload))
      })
    })
  }, [dispatch]);

  useEffect(() => {
    // This effect applies when issues are created, affecting the length of issuesInExperiment.
    // When issues are editing it will not fire, but state hooks will handle updating values.
    if (experimentDetails && issuesInExperiment) {
      uploadIssues(issuesInExperiment, experimentDetails.issuesKey);
      refreshExperiment();
    }
  }, [issuesInExperiment])

  const uploadIssues = async (issues, issuesKey) => {
    try {
      let jsonIssues = JSON.parse(JSON.stringify(
        issues,
        (key, value) => {
          if (key == "experimentEntry") return undefined;
          else return value;
        }
      ));

      await pushNewS3Object(issuesKey, '{"issueLog": ' + JSON.stringify(jsonIssues) + '}');
    } catch (errror) {
      signOut();
      navigate(`/`);
    }
  }

  const addIssueNote = async (issuesKey, id, newNote) => {
    dispatch(issueNoteAdded({ id, newNote, issuesKey }))
    refreshExperiment();
    uploadIssues(issuesInExperiment, experimentDetails.issuesKey);
  }

  const resolveIssue = async (issuesKey, id) => {
    dispatch(issueResolved({ id, issuesKey }))
    uploadIssues(issuesInExperiment, experimentDetails.issuesKey);
    refreshExperiment();
  }

  const reportAnIssue = async (id, title) => {
    dispatch(issueCreated(
      new Issue(id, title, false, [new Message(Date.now(), "Issue opened by " + user + ".").toJson()], experimentDetails)
    ))
  }

  const refreshExperiment = async () => {
    try {
      if (!experimentDetails) { return; }

      if (experimentDetails.s3Files.includes(experimentDetails.dataKey)) {
        let data = parseCsvString(await getS3Object(experimentDetails.dataKey));
        setData(data);
      }

      if (experimentDetails.s3Files.includes(experimentDetails.metadataKey)) {
        let _notes = await getS3Object(experimentDetails.metadataKey);
        setNotes(Notes.fromJson(parseJsonString(_notes)));
      }

      if (experimentDetails.s3Files.includes(experimentDetails.issuesKey)) {
        let entries = [];
        for (let issue of issuesInExperiment) {
          entries.push(IssueEntry.fromJson(
            issue,
            (id, newNote) => { addIssueNote(experimentDetails.issuesKey, id, newNote) },
            (id) => { resolveIssue(experimentDetails.issuesKey, id) }
          ));
        }
        setIssueEntries(entries);
      }
    } catch (error) {
      signOut()
      navigate(`/`);
    }
  }

  return (
    <>
      <div id="details">
        {data ? (<div className="experimentDisplay">
          <Graph name={experimentDetails ? experimentDetails.name : 'Name not found'} data={data ? data : null} />

          <div className="logs">
            <div className="log">
              <div className="log-header">
                <h1>Notes</h1>
                <BasicPopover content="Experiment notes can be entered during the experiment by the operator. These usually describe actions performed." />
              </div>
              {notes ? notes.reactObj : <p>No Notes to display</p>}

            </div>
            <div className="log">
              <div className="log-header">
                <h1>Issues</h1>
                <div style={{ display: "flex", flexDirection: "row" }}>
                  <FlagIssue reportIssueFn={(issueId, title) => { reportAnIssue(issueId, title) }} issueId={nextIssueId}></FlagIssue>
                  <BasicPopover content="Issues identified during the experiment can be reported and monitored here. Issues added or modified are visible to the operator." />
                </div>
              </div>
              {issueEntries ? issueEntries.map((issueEntry) => issueEntry.reactObj) : <p>No Issues to display</p>}
            </div>
          </div>

        </div>) : (<>


        </>)}
      </div>
    </>
  )
}